import { useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { PageHeader, StatusBadge, Loading } from "@/components/common";
import { api, useFetch, errMsg, money, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

export default function Billing() {
  const { session, refresh } = useAuth();
  const { data: plans } = useFetch("/public/plans");
  const { data: history } = useFetch("/billing/history");
  const [busy, setBusy] = useState(null);
  const isOwner = session.user.id === session.business.owner_user_id;
  const choose = async (p) => {
    setBusy(p.id);
    try {
      const r = await api.post("/billing/checkout", { plan_id: p.id, origin_url: window.location.origin });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
      else { toast.success(`Switched to ${p.name}`); await refresh(); setBusy(null); }
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(null);
    }
  };
  return (
    <div data-testid="billing-page">
      <PageHeader eyebrow="Subscription" title="Plan & billing"
        subtitle={<>You're on <b data-testid="current-plan-name">{session.plan?.name || "no plan"}</b>{session.business.subscription_status === "active" && " · subscription active"}. Payments are processed securely by Stripe.</>} />
      {!plans ? <Loading /> : (
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {plans.map((p) => {
            const current = p.id === session.business.plan_id;
            return (
              <div key={p.id} className={cn("panel flex flex-col", current && "border-2 border-emerald-700")} data-testid={`billing-plan-${p.name}`}>
                <div className="flex justify-between items-center"><h3 className="text-2xl font-bold">{p.name}</h3>{current && <StatusBadge status="active" label="Current" />}</div>
                <p className="text-sm text-slate-500 mt-1">{p.description}</p>
                <div className="font-display text-3xl font-extrabold mt-4">{p.price_monthly > 0 ? money(p.price_monthly, p.currency) : "Free"}<span className="text-sm font-medium text-slate-500">{p.price_monthly > 0 && " /month"}</span></div>
                <ul className="mt-4 space-y-2 text-sm flex-1">{p.features.map((f) => <li key={f} className="flex gap-2"><Check className="w-4 h-4 text-emerald-700 mt-0.5" />{f}</li>)}</ul>
                <Button disabled={current || !isOwner || busy} onClick={() => choose(p)} className="mt-6 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`choose-plan-${p.name}`}>
                  {current ? "Current plan" : busy === p.id ? "Redirecting…" : p.price_monthly > 0 ? "Upgrade with Stripe" : "Switch to free"}
                </Button>
              </div>
            );
          })}
        </div>
      )}
      <h3 className="text-xl font-bold mb-3">Payment history</h3>
      <div className="panel p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Plan</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {(history || []).map((h) => (
              <TableRow key={h.session_id}><TableCell>{fmtDate(h.created_at)}</TableCell><TableCell>{h.plan_name}</TableCell><TableCell>{money(h.amount, h.currency)}</TableCell><TableCell><StatusBadge status={h.payment_status} /></TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
        {history?.length === 0 && <p className="text-sm text-slate-500 p-6">No payments yet.</p>}
      </div>
    </div>
  );
}
